import { memo, useCallback, type CSSProperties } from 'react'
import type { AiSuggestedProgram } from '../../types/domain.js'
import { useAgendaView } from '../../store/index.js'
import { clampPpm, dayY, GUTTER_WIDTH, TIME_COL_WIDTH } from './useAgendaLayout.js'

interface SuggestionOverlayProps {
  suggestions:     AiSuggestedProgram[]
  acceptedIndexes: Set<number>
  onToggle:        (index: number) => void
}

export const SuggestionOverlay = memo(function SuggestionOverlay({
  suggestions, acceptedIndexes, onToggle,
}: SuggestionOverlayProps) {
  const agendaView = useAgendaView()
  const pxPerHour  = clampPpm(agendaView.pxPerMinute) * 60

  return (
    <div
      className="suggestion-overlay"
      // La gouttiere des liaisons s'intercale entre l'axe et les blocs : sans
      // elle dans le compte, les cartes de suggestion tombent a cote.
      style={{ '--suggestion-time-column': `${TIME_COL_WIDTH + GUTTER_WIDTH}px` } as CSSProperties}
    >
      {suggestions.map((s, i) => {
        const top      = dayY(s.startTime, agendaView.date, pxPerHour)
        const height   = Math.max(20, Math.round((s.durationSec / 3600) * pxPerHour) - 2)

        return (
          <SuggestionCard
            key={i}
            accepted={acceptedIndexes.has(i)}
            height={height}
            index={i}
            onToggle={onToggle}
            reason={s.reason}
            title={s.programTitle}
            top={top + 1}
          />
        )
      })}
    </div>
  )
})

const SuggestionCard = memo(function SuggestionCard({
  accepted, height, index, onToggle, reason, title, top,
}: {
  accepted: boolean
  height: number
  index: number
  onToggle: (index: number) => void
  reason: string
  title: string
  top: number
}) {
  const handleToggle = useCallback(() => onToggle(index), [index, onToggle])

  return (
    <button
      aria-pressed={accepted}
      className={`suggestion-overlay__card${accepted ? ' is-accepted' : ''}`}
      onClick={handleToggle}
      title={reason}
      style={{ '--suggestion-height': `${height}px`, '--suggestion-top': `${top}px` } as CSSProperties}
      type="button"
    >
      <span className="suggestion-overlay__meta">
        <span className="suggestion-overlay__badge">✦ IA</span>
        {accepted && <span className="suggestion-overlay__accepted">✓ Accepté</span>}
      </span>
      {height > 22 && <span className="suggestion-overlay__title">{title}</span>}
      {height > 42 && <span className="suggestion-overlay__reason">{reason}</span>}
    </button>
  )
})
